import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { Callout } from "@/components/calv/Callout";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/leistungen/performance-marketing/meta-ads/")({
  head: () => {
    const h = pageHead("pmMetaAds", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Meta Ads Agentur",
          description: "Facebook, Instagram, Advantage+ — gesteuert mit Creative-Disziplin, sauberer Attribution und eigenem Tooling für Bulk-Operationen.",
          url: "/leistungen/performance-marketing/meta-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "Performance Marketing", path: "/leistungen/performance-marketing" },
          { name: "Meta Ads", path: "/leistungen/performance-marketing/meta-ads" },
        ])),
      ],
    };
  },
  component: MetaAdsDE,
});

const steps = [
  { num: "01", title: "Analyse (3–5 Tage)", body: "Account-Audit, Creative-Performance-Analyse, Conversions-API-Setup-Check, Datenqualitäts-Audit (Match Quality Scores), Pixel-Hygiene-Prüfung. Output: konkrete Hypothesen, wo Effizienz verloren geht — und ob das überwiegend ein Creative-, Tracking- oder Strukturproblem ist. Klare Diagnose, nicht generischer Audit-Bericht." },
  { num: "02", title: "Setup, Härtung & erste Creatives (1–2 Wochen)", body: "Conversions API über Server-side Tracking härten. Events Manager auf Domain-Verifizierung und Aggregated Events Konfiguration prüfen. Account-Struktur an wirtschaftlicher Logik ausrichten — Top-of-Funnel, Retargeting-Stufen, CLV-Segmentierung. Parallel erste Creative-Test-Batches über picazzoo aufsetzen — bei Meta startet die Creative-Iteration ab Tag 1, nicht später." },
  { num: "03", title: "Creative-Iteration im Wochenrhythmus (ab Woche 2–3)", body: "Wöchentlicher Creative-Test-Rhythmus mit klaren Hypothesen. picazzoo für skalierbare Asset-Variation. Iteration auf Hook-, Format- und Audience-Match-Variation. Im Meta-Ökosystem ist die Geschwindigkeit der Creative-Iteration der Hauptfaktor für Performance-Skalierung — wer wöchentlich iteriert, schlägt wer monatlich iteriert." },
  { num: "04", title: "Skalierung & laufender Betrieb (kontinuierlich)", body: "Budget-Skalierung in funktionierenden Audience-Creative-Kombinationen. Advantage+ selektiv dort, wo Datenqualität es trägt. Monatlicher Performance-Review auf wirtschaftlichen Kennzahlen, nicht Plattform-KPIs. Operative Steuerung direkt durch den Geschäftsführer — nicht durch Junior-Account-Manager mit hoher Mandate-Last." },
];

const capabilities = [
  { name: "Conversions API & Tracking", detail: "Server-side · Match Quality" },
  { name: "Creative-Test-Pipelines", detail: "Hypothesen-Logik · Lernzyklen" },
  { name: "Asset-Produktion mit picazzoo", detail: "KI · markentreu · skalierbar" },
  { name: "Account-Operations mit GonzalAds", detail: "Bulk · Automation Rules" },
  { name: "Advantage+ Shopping selektiv", detail: "Wo Datenqualität es trägt" },
  { name: "Audience-Strategien", detail: "Lookalikes · Custom · CRM" },
  { name: "Funnel-Architektur", detail: "Awareness · Consideration · Conversion" },
  { name: "Reporting & Steuerung", detail: "Wirtschaftlich + Plattform-Diagnostik" },
];

const audience = [
  { tag: "Primär", title: "B2C E-Commerce", body: "Wichtigster Social-Media-Performance-Kanal für die meisten E-Commerce-Setups, vor allem bei visuell vermittelbaren Produkten (Mode, Kosmetik, Home, Sport, Pet). Skalierbar von €1.500/Monat bis sechsstellig." },
  { tag: "Sekundär", title: "SaaS B2C", body: "Funktioniert für SaaS-Produkte, die einer breiten Konsumenten-Zielgruppe dienen — Apps, Tools, Subscriptions im Lifestyle-Bereich. Trial-Sign-up muss sauber getrackt sein." },
  { tag: "Eingeschränkt", title: "B2B mit visuellem Hebel", body: "Funktioniert für B2B-Setups, wenn die Zielgruppe auch privat aktiv ist und das Produkt visuell vermittelbar ist. Für klassisches Enterprise-B2B ist LinkedIn deutlich besser geeignet." },
  { tag: "Disqualifikation", title: "Wann wir nicht der richtige Partner sind", body: "Monatsbudgets unter €1.500 — der operative Aufwand für sauberes Setup ist hoch. Erwartung 'virale Posts' — wir machen Performance, kein Influencer-Management. Ohne starke Visuals ist Meta selten die richtige Wahl." },
];

const realistic = [
  "ROAS-Verbesserungen von 30–60 % in B2C-E-Commerce-Setups innerhalb von 3–4 Monaten sind realistisch, wenn vorher mit ungehärteter Conversions-API-Anbindung und ohne systematische Creative-Iteration gearbeitet wurde.",
  "Match-Quality-Score-Verbesserungen von 6–7 auf 8–9 sind in den meisten Setups erreichbar — und übersetzen sich direkt in bessere Bid-Algorithmen-Performance.",
  "Operative Zeit-Reduktion von 70–90 % bei Asset-Produktion und Kampagnen-Aufbau durch Einsatz von picazzoo und GonzalAds.",
  "Skalierungs-Hebel von 3–5x bei gleichem ROAS sind möglich, wenn die Creative-Pipeline funktioniert und Customer-Economics es tragen.",
];

const value = [
  ["Creative-Pipeline statt Designer-Bottleneck.", "Über picazzoo produzieren wir markentreue Asset-Variationen in Stückzahlen, die in Designer-Workflows wirtschaftlich nicht erreichbar sind."],
  ["Account-Operations 80–90 % schneller.", "Über GonzalAds bauen wir Kampagnen, Asset-Sets und Automation-Rules in einem Bruchteil der Zeit des Native-Ads-Manager."],
  ["Saubere Attribution trotz iOS-Datenverlust.", "Server-side Conversions API mit aktivem Match-Quality-Monitoring. Keine 'Setup einmal, dann hoffen'-Logik."],
  ["Wirtschaftliche Steuerung statt Plattform-Vanity.", "Wir reporten auf Deckungsbeitrag und Customer-LTV, nicht auf CTRs oder Plattform-ROAS."],
  ["Operative Steuerung durch erfahrene Hand.", "Direkt durch den Geschäftsführer — sichtbar in Reaktionszeit und Strategie-Tiefe."],
  ["Vergütungs-Modell ohne Budget-Kopplung.", "Wir verdienen nicht mehr, wenn Sie mehr Budget einsetzen."],
];

const related = [
  { to: "/leistungen/performance-marketing/google-ads", name: "Google Ads", sub: "Search, Shopping und PMax als komplementärer Performance-Kanal" },
  { to: "/leistungen/picazzoo", name: "picazzoo", sub: "Unsere KI-Pipeline für markentreue Visual Assets" },
  { to: "/leistungen/conversion-rate-optimierung", name: "Conversion-Rate-Optimierung", sub: "Conversion-Hebel auf der Landing-Page für höhere Meta-Wirtschaftlichkeit" },
];

const channelLinks = [
  { to: "/leistungen/performance-marketing/tiktok-ads", name: "TikTok Ads", sub: "Performance auf TikTok jenseits der Influencer-Logik." },
  { to: "/leistungen/performance-marketing/youtube-demand-gen", name: "YouTube Demand Gen", sub: "Video-Performance auf YouTube für Awareness und Mid-Funnel-Conversion." },
  { to: "/leistungen/performance-marketing/google-ads", name: "Google Ads", sub: "Search, Shopping, PMax und Demand Gen als komplementärer Performance-Kanal." },
  { to: "/leistungen/performance-marketing/affiliate-marketing", name: "Affiliate-Marketing", sub: "Partner-gesteuerte Performance mit eigener Tracking- und Provisions-Disziplin." },
];

function MetaAdsDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Meta Ads"
          headline={<>Meta Ads Agentur — <em>Performance ohne Plattformlogik,</em> mit Creative-Disziplin.</>}
          lead="Facebook, Instagram, Advantage+ — gesteuert mit Creative-Disziplin, server-side Tracking und eigenem Bulk-Tool. Calvarius arbeitet seit 2013 als Meta Ads Agentur für E-Commerce, B2C-SaaS und visuell vermittelbare B2B-Setups — ohne Boost-Button-Mentalität, ohne Plattform-Mythen."
          metaStrip={["Seit 2013 im Kanal", "Bis €100K/Monat Budgets", "Eigenes Bulk-Tool"]}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Performance Marketing", to: "/leistungen/performance-marketing" },
            { label: "Meta Ads" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Was eine Meta Ads Agentur heute <em>wirklich erfordert.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wer eine Meta Ads Agentur sucht, hat in der Regel bereits eine klare Erfahrung mit der Plattform — und meistens eine gemischte. Meta hat in den letzten Jahren seine Steuerbarkeit drastisch reduziert. iOS 14.5 hat die Datenqualität halbiert, Advantage+ Shopping nimmt manuelle Kontrolle weg, und die Plattform empfiehlt aggressiv Ad-Set-Konsolidierungen, die für Meta wirtschaftlich sind, aber für viele Werbetreibende nicht. Gleichzeitig ist Meta für die meisten B2C-E-Commerce-Mandate nach wie vor unverzichtbar — die Reichweite, die Audience-Qualität und die Skalierungsmöglichkeiten finden sich in keinem anderen Social-Media-Kanal.</p>
              <p className="mb-5">Calvarius arbeitet im Meta-Ökosystem mit drei Disziplinen, die in den meisten Mandaten den Unterschied machen. Erstens: <strong className="font-semibold text-ink">Creative-Disziplin</strong> als Hauptsteuerungs-Hebel — Creatives sind heute der wichtigste Performance-Faktor, wichtiger als Targeting, wichtiger als Bid-Strategien. Zweitens: <strong className="font-semibold text-ink">saubere Attribution</strong> trotz iOS-Datenverlust — Conversions API, server-side Events Manager-Integration und Match-Quality-Optimierung sind Pflicht, nicht Kür. Drittens: <strong className="font-semibold text-ink">Account-Struktur</strong>, die Plattform-Empfehlungen nicht blind folgt — Advantage+ ist ein Tool, kein Glaubensbekenntnis.</p>
              <p>Konkret heißt das: Wir produzieren über <Link to="/leistungen/picazzoo" className="underline">picazzoo</Link> Creative-Varianten in Markentonalität — KI-gestützt, aber markentreu, in Stückzahlen, die in herkömmlichen Designer-Workflows nicht erreichbar wären. Wir steuern Account-Operationen über <Link to="/leistungen/gonzalads" className="underline">GonzalAds</Link>, das Bulk-Operationen, Asset-Management und Automation-Rules in einer Geschwindigkeit ermöglicht, die im Native-Ads-Manager nicht möglich ist. Und wir tracken über server-side Conversions API mit Match-Quality-Scores, die wir aktiv überwachen — nicht „wir haben es eingerichtet, also läuft es".</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Zielgruppen-Einordnung" headline={<>Wann Sie eine Meta Ads Agentur <em>brauchen — und wann nicht.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodik" headline={<>Wie unsere Meta Ads Agentur <em>Mandate startet.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Schneller Start als Wettbewerbs-Vorteil</p>
              <p className="text-[15px] leading-relaxed text-ink-2">Während klassische Großagenturen häufig vier bis acht Wochen brauchen, bevor die erste Creative-Iteration läuft, sind wir mit wöchentlichen Test-Zyklen ab Woche zwei bis drei produktiv. Möglich ist das, weil unser Tooling — picazzoo für Asset-Produktion, GonzalAds für Account-Operations — den klassischen Designer- und Account-Manager-Bottleneck eliminiert, und weil der Geschäftsführer direkt operativ arbeitet, nicht über Stakeholder-Schichten.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Was wir liefern" headline={<>Was unsere Meta Ads Agentur <em>konkret liefert.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools und Stack unserer <em>Meta-Ads-Steuerung.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Plattform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Meta Ads Manager (selektiv)</li>
                  <li>Meta Business Suite</li>
                  <li>Events Manager</li>
                  <li>Pixel Helper</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side Conversions API</li>
                  <li>Google Tag Manager (Web + Server)</li>
                  <li>Google Analytics 4</li>
                  <li>Match Quality Score Monitoring</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius-Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/leistungen/gonzalads" className="font-medium text-ink hover:text-cyan-deep">GonzalAds →</Link><br/><span className="text-ink-3">Speed-Tool für Meta-Ads-Operations</span></li>
                  <li><Link to="/leistungen/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">Visual-Production-Pipeline mit Brand-DNA</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Was realistisch ist" headline={<>Realistische Ergebnisse einer <em>Meta-Ads-Optimierung.</em></>}
          caption="Diese Bandbreiten sind Erfahrungswerte. Je nach Ausgangslage können Hebel größer oder kleiner ausfallen.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Was Sie gewinnen" headline={<>Was Sie von unserer <em>Meta Ads Agentur</em> bekommen.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="de" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Weiter vertiefen" headline={<>Verwandte Disziplinen.</>}
          caption="Diese Themen wirken im Zusammenspiel.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/leistungen/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← Alle Performance-Marketing-Disziplinen
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Spezial-Anwendung" headline={<>Spezial-Anwendung: <em>Mitarbeiter-Gewinnung.</em></>}>
          <RevealOnScroll>
            <div className="max-w-2xl">
              <p className="text-[17px] leading-relaxed text-ink-2">
                Recruiting über Meta Ads ist seit 2018 eine eigenständige Disziplin in Calvarius-Mandaten — mit eigenen Targeting-Mechaniken, eigenen Funnel-Anforderungen und branchen-spezifischen Cost-per-Application-Werten. Für mittelständische Mandate aus Pflege, Handwerk, Logistik, Produktion, Gastronomie und pädagogischen Berufen ist Meta Ads die wirtschaftlich rationalste Recruiting-Infrastruktur — bei korrektem Targeting mit 60–90 Prozent niedrigerer CPA als klassische Stellen-Portale.
              </p>
              <Link
                to="/leistungen/performance-marketing/meta-ads/recruiting"
                className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline"
              >
                Mehr zu Meta Ads für Mitarbeiter-Gewinnung →
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="11" kicker="Konkret werden">
          <Callout>
            <strong>Im strategischen Zusammenhang:</strong> Meta Ads ist die Performance-Tactic für bezahlte Reichweite auf Instagram und Facebook. Wer die <em>integrierte</em> Social-Methodik sucht — also Organic-Content, Paid-Verstärkung und Community-Substanz als gemeinsames System — findet sie unter <Link to="/leistungen/social-media-marketing" className="underline">Social Media Marketing →</Link>.
          </Callout>
          <CTABlock
            headline={<>Meta Ads Audit in 30 Minuten — <em>kostenlos und unverbindlich.</em></>}
            body="In einem ersten Gespräch klären wir, ob Meta Ads in Ihrem Setup wirtschaftlich Sinn macht — und wenn ja, mit welcher Priorität. Kostenlos, unverbindlich, ohne Sales-Pitch."
            primary={{ label: "Gespräch vereinbaren", to: "/kontakt" }}
            secondary={{ label: "Performance-Marketing-Übersicht", to: "/leistungen/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
